import { useState, useRef, useEffect, useCallback } from 'react';
import EmojiPicker, { EmojiClickData, Theme } from 'emoji-picker-react';
import type { EmojiData } from 'emoji-picker-react/dist/types/exposedTypes';
import { isDarkMode } from 'utils/validators';
import BaseButton from 'components/ui/BaseButton';
import { t } from 'services/i18n.service';
import { useAppSelectorShallow } from 'store/hooks';
import { habitEmojiPickerSelector } from 'store/reducer/setting/selectors';

const loadEmojiData = async (language: string): Promise<EmojiData> => {
  if (language.startsWith('es')) {
    const mod = await import('emoji-picker-react/dist/data/emojis-es');
    return mod.default;
  }
  const mod = await import('emoji-picker-react/dist/data/emojis-en');
  return mod.default;
};

interface HabitEmojiPickerProps {
  emoji: string;
  readOnly?: boolean;
  onEmojiClick?: () => void;
  onEmojiChange?: (emoji: string) => void;
}

type PickerState = {
  isOpen: boolean;
  emojiData?: EmojiData;
};

const HabitEmojiPicker: React.FC<HabitEmojiPickerProps> = ({
  emoji,
  readOnly = false,
  onEmojiClick,
  onEmojiChange
}) => {
  const { language } = useAppSelectorShallow(habitEmojiPickerSelector);
  const [pickerState, setPickerState] = useState<PickerState>({
    isOpen: false,
    emojiData: undefined
  });
  const emojiButtonRef = useRef<HTMLButtonElement>(null);
  const pickerContainerRef = useRef<HTMLDivElement>(null);

  const closePicker = useCallback(() => {
    setPickerState((state) => ({ ...state, isOpen: false }));
  }, []);

  const handleEmojiButtonClick = () => {
    if (readOnly) {
      onEmojiClick?.();
      return;
    }
    if (onEmojiClick) {
      onEmojiClick();
    } else {
      setPickerState((state) => ({ ...state, isOpen: !state.isOpen }));
    }
  };

  const handleEmojiSelect = useCallback(
    (emojiClickData: EmojiClickData) => {
      onEmojiChange?.(emojiClickData.emoji);
      closePicker();
    },
    [onEmojiChange, closePicker]
  );

  useEffect(() => {
    if (!pickerState.isOpen) return;

    let cancelled = false;

    loadEmojiData(language)
      .then((data) => {
        if (!cancelled) {
          setPickerState((state) => ({ ...state, emojiData: data }));
        }
      })
      .catch((error) => {
        console.error('Failed to load emoji picker data:', error);
      });

    return () => {
      cancelled = true;
    };
  }, [pickerState.isOpen, language]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        pickerContainerRef.current &&
        !pickerContainerRef.current.contains(event.target as Node) &&
        emojiButtonRef.current &&
        !emojiButtonRef.current.contains(event.target as Node)
      ) {
        closePicker();
      }
    };

    if (pickerState.isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [pickerState.isOpen, closePicker]);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && pickerState.isOpen) {
        closePicker();
      }
    };

    if (pickerState.isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => {
        document.removeEventListener('keydown', handleEscape);
      };
    }
  }, [pickerState.isOpen, closePicker]);

  return (
    <div className='flex flex-col items-center ~gap-1.5/3 flex-shrink-0'>
      <BaseButton
        ref={emojiButtonRef}
        disabled={readOnly}
        variant='tertiary'
        onClick={handleEmojiButtonClick}
        aria-label={t('settings_page.select_habit_emoji')}
        icon={
          <span
            className={`~text-base/2xl ${emoji ? '' : 'text-gray-400 dark:text-secondary-dark-600'}`}>
            {emoji || '+'}
          </span>
        }
        additionalStyles='!p-0 !~py-2.5/3 !~px-1/1.5'
      />
      {pickerState.isOpen && (
        <div
          ref={pickerContainerRef}
          className='absolute z-50 ~top-12/14 left-0 ~mt-1/2 ~text-sm/base'
          style={{ transform: 'translateX(0)' }}>
          {pickerState.emojiData ? (
            <EmojiPicker
              emojiData={pickerState.emojiData}
              onEmojiClick={handleEmojiSelect}
              theme={isDarkMode() ? Theme.DARK : Theme.LIGHT}
              previewConfig={{
                showPreview: false
              }}
              searchDisabled={false}
              skinTonesDisabled={true}
              searchPlaceholder={t('search')}
            />
          ) : null}
        </div>
      )}
    </div>
  );
};

export default HabitEmojiPicker;
